import {
  EuiFlexGroup,
  EuiSuperSelect,
  EuiSuperSelectOption,
  EuiToken,
} from "@inscopix/ideas-eui";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { DataTableColumnDefinition } from "pages/gdt/store/DataTableProvider.types";
import { COLUMN_KIND_MAP } from "pages/gdt/modals/ModalCreateColumn/ModalCreateColumn";

interface FieldColumnCompressedProps {
  readOnly?: boolean;
  noItemsMessage?: string;
  isColumnSelectable: (definition: DataTableColumnDefinition) => boolean;
  tableId: string | undefined;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
}

export const FieldColumnCompressed = ({
  readOnly = false,
  noItemsMessage = "No columns found",
  isColumnSelectable,
  tableId,
  value,
  onChange,
  disabled = false,
}: FieldColumnCompressedProps) => {
  const columns = useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === tableId);
    return (table?.columns ?? []).filter((c) =>
      isColumnSelectable(c.definition),
    );
  });

  // Disabled option displayed when tables exist
  const noItemsOption = {
    value: "no_items",
    disabled: true,
    inputDisplay: noItemsMessage,
  };

  const options: EuiSuperSelectOption<string>[] = columns.map((column) => ({
    value: column.id,
    inputDisplay: (
      <EuiFlexGroup
        alignItems="center"
        gutterSize="s"
        responsive={false}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <EuiToken iconType={COLUMN_KIND_MAP[column.definition.kind].icon} />
        <span>{column.name}</span>
      </EuiFlexGroup>
    ),
  }));

  const handleChange = (newColumnId: string) => {
    if (value === newColumnId) {
      onChange(undefined);
    } else {
      onChange(newColumnId);
    }
  };

  // Render the input display for the select option when in read-only mode
  if (readOnly) {
    const selectedOption = options.find((option) => option.value === value);
    return <>{selectedOption?.inputDisplay ?? null}</>;
  }

  return (
    <EuiSuperSelect
      popoverProps={{ style: { width: "100%" } }}
      compressed
      fullWidth
      options={options.length > 0 ? options : [noItemsOption]}
      valueOfSelected={value}
      onChange={handleChange}
      placeholder="None"
      disabled={disabled}
    />
  );
};
